import { useRef, useEffect, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon } from 'lucide-react';

// Lightweight, dependency-free rich text editor (contentEditable + execCommand).
// Emits HTML via onChange. Supports Gmail-style font family + size, bold/italic/underline,
// lists and links. Font styling is applied as inline CSS so it persists in the sent email HTML.

// Gmail-equivalent font choices mapped to email-safe font stacks.
const FONTS = [
  { label: 'Sans Serif', value: "Arial, Helvetica, sans-serif" },
  { label: 'Serif', value: "Georgia, 'Times New Roman', serif" },
  { label: 'Fixed Width', value: "'Courier New', Courier, monospace" },
  { label: 'Wide', value: "'Arial Black', Gadget, sans-serif" },
  { label: 'Narrow', value: "'Arial Narrow', Arial, sans-serif" },
  { label: 'Comic Sans MS', value: "'Comic Sans MS', 'Comic Sans', cursive" },
  { label: 'Garamond', value: "Garamond, Baskerville, 'Times New Roman', serif" },
  { label: 'Georgia', value: "Georgia, serif" },
  { label: 'Tahoma', value: "Tahoma, Geneva, sans-serif" },
  { label: 'Trebuchet MS', value: "'Trebuchet MS', Helvetica, sans-serif" },
  { label: 'Verdana', value: "Verdana, Geneva, sans-serif" },
];
// Gmail-equivalent sizes -> legacy execCommand fontSize scale (rendered as CSS keywords).
const SIZES = [
  { label: 'Small', value: '2' },
  { label: 'Normal', value: '3' },
  { label: 'Large', value: '5' },
  { label: 'Huge', value: '6' },
];

const isEmptyHtml = (h) =>
  !h || h.replace(/<br\s*\/?>(?=\s*$)/gi, '').replace(/<div>|<\/div>|<p>|<\/p>|&nbsp;/gi, '').trim() === '';

export function RichTextEditor({
  value = '', onChange, placeholder = 'Write...', className = '', minHeight = 140, testid = 'rich-text-editor',
}) {
  const ref = useRef(null);
  const savedRange = useRef(null);

  // Sync external value changes (e.g., reset to '') without disrupting active typing.
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && (value || '') !== el.innerHTML) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el || !onChange) return;
    const html = el.innerHTML;
    onChange(isEmptyHtml(html) ? '' : html);
  }, [onChange]);

  // Persist the caret/selection so toolbar controls (which steal focus) can restore it.
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (ref.current && ref.current.contains(r.commonAncestorContainer)) {
        savedRange.current = r.cloneRange();
      }
    }
  };

  const restoreSelection = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const exec = (cmd, arg, useCSS = false) => {
    restoreSelection();
    if (useCSS) document.execCommand('styleWithCSS', false, true);
    document.execCommand(cmd, false, arg);
    if (useCSS) document.execCommand('styleWithCSS', false, false);
    saveSelection();
    emit();
  };

  const addLink = () => {
    saveSelection();
    const url = window.prompt('Enter a URL (e.g. https://example.com)');
    if (!url) return;
    exec('createLink', /^https?:\/\//i.test(url) ? url : `https://${url}`);
  };

  const Btn = ({ onClick, icon: Icon, label, tid }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={label}
      aria-label={label}
      className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
      data-testid={`${testid}-${tid}`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );

  const selectCls = "h-7 rounded border border-border bg-background text-foreground text-xs px-1 max-w-[104px] focus:outline-none";

  return (
    <div className={`rounded-md border border-border bg-background ${className}`} data-testid={testid}>
      <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1 flex-wrap">
        <select
          className={selectCls}
          defaultValue=""
          onMouseDown={saveSelection}
          onChange={(e) => { const v = e.target.value; e.target.value = ''; if (v) exec('fontName', v, true); }}
          title="Font"
          data-testid={`${testid}-font`}
        >
          <option value="" disabled>Font</option>
          {FONTS.map(f => <option key={f.label} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>)}
        </select>
        <select
          className={selectCls}
          defaultValue=""
          onMouseDown={saveSelection}
          onChange={(e) => { const v = e.target.value; e.target.value = ''; if (v) exec('fontSize', v, true); }}
          title="Size"
          data-testid={`${testid}-size`}
        >
          <option value="" disabled>Size</option>
          {SIZES.map(s => <option key={s.label} value={s.value}>{s.label}</option>)}
        </select>
        <span className="w-px h-4 bg-border mx-1" />
        <Btn onClick={() => exec('bold')} icon={Bold} label="Bold" tid="bold" />
        <Btn onClick={() => exec('italic')} icon={Italic} label="Italic" tid="italic" />
        <Btn onClick={() => exec('underline')} icon={Underline} label="Underline" tid="underline" />
        <span className="w-px h-4 bg-border mx-1" />
        <Btn onClick={() => exec('insertUnorderedList')} icon={List} label="Bullet list" tid="ul" />
        <Btn onClick={() => exec('insertOrderedList')} icon={ListOrdered} label="Numbered list" tid="ol" />
        <span className="w-px h-4 bg-border mx-1" />
        <Btn onClick={addLink} icon={LinkIcon} label="Insert link" tid="link" />
      </div>
      <div className="relative">
        {isEmptyHtml(value) && (
          <span className="pointer-events-none absolute left-3 top-2 text-sm" style={{ color: '#CFCFCF' }}>{placeholder}</span>
        )}
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={() => { saveSelection(); emit(); }}
          onMouseUp={saveSelection}
          onKeyUp={saveSelection}
          role="textbox"
          aria-multiline="true"
          className="prose-editor px-3 py-2 text-sm text-foreground outline-none overflow-y-auto"
          style={{ minHeight }}
          data-testid={`${testid}-input`}
        />
      </div>
    </div>
  );
}

export default RichTextEditor;
